import { fontSize, fontWeight, theme } from "@tm/utility/global";
import styled from "styled-components";
import { deviceType } from "../../../../utils";

export const InfoPage = styled.div`
  background: ${theme.smokeWhite};
  min-height: 100vh;
`;

export const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 1200px;
  margin: 0 auto;
  padding: 24px 16px 40px;

  @media ${deviceType.mobile} {
    width: 100%;
    padding: 16px;
  }
`;

/* ── Page Header (outside white card) ──────────────────────────────── */

export const PageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

export const PageIcon = styled.img`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  margin-top: 4px;
`;

export const PageTitleGroup = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  line-height: normal;
`;

export const PageTitle = styled.h2`
  margin: 0;
  font-size: ${fontSize.font24};
  font-weight: ${fontWeight.black};
  line-height: 1;
  color: ${theme.blackText};

  @media ${deviceType.mobile} {
    font-size: ${fontSize.font20};
  }
`;

export const PageSubtitle = styled.p`
  margin: 0;
  font-size: ${fontSize.font18};
  font-weight: ${fontWeight.regular};
  line-height: 1;
  color: ${theme.greyText6};

  @media ${deviceType.mobile} {
    font-size: ${fontSize.font14};
  }
`;

/* ── Content Grid ──────────────────────────────────────────────────── */

export const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: 16px;
  align-items: start;

  @media ${deviceType.mobile} {
    grid-template-columns: 1fr;
  }
`;

export const InfoLeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const InfoRightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  position: sticky;
  top: 24px;

  @media ${deviceType.mobile} {
    position: static;
  }
`;

/* ── Details Card (white card with sections only) ──────────────────── */

export const DetailsCard = styled.div`
  background: ${theme.whiteText};
  border-radius: 16px;
  overflow: hidden;
  padding: 24px;

  @media ${deviceType.mobile} {
    padding: 16px;
  }
`;

export const SectionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const SectionSeparator = styled.hr`
  border: none;
  border-top: 1px solid ${theme.disabledGrey2};
  margin: 0;
  width: 100%;
`;

export const SectionWrapper = styled.div`
  display: flex;
  gap: 12px;
`;

export const SectionNumber = styled.span`
  font-size: ${fontSize.font18};
  font-weight: ${fontWeight.black};
  color: ${theme.blackText};
  flex-shrink: 0;
`;

export const SectionContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const SectionTitle = styled.h3`
  margin: 0;
  font-size: ${fontSize.font18};
  font-weight: ${fontWeight.black};
  color: ${theme.blackText};
`;

export const SectionItemsGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const SectionItemBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const SectionItemName = styled.p`
  margin: 0;
  font-size: ${fontSize.font14};
  font-weight: ${fontWeight.bold};
  color: ${theme.blackText};
`;

export const SectionItemDesc = styled.div`
  margin: 0;
  font-size: ${fontSize.font14};
  font-weight: ${fontWeight.regular};
  color: ${theme.greyText};

  a {
    color: ${theme.blueText};
    text-decoration: underline;
  }
`;

/* ── Help Card ─────────────────────────────────────────────────────── */

export const HelpCard = styled.div<{ $bgColor?: string }>`
  background: ${({ $bgColor }) => $bgColor || theme.yellowText2};
  border-radius: 8px;
  padding: 20px;
`;

export const HelpTitle = styled.h3`
  margin: 0 0 16px;
  font-size: ${fontSize.font18};
  font-weight: ${fontWeight.black};
  color: ${theme.blackText};
`;

export const HelpContactText = styled.div`
  font-size: ${fontSize.font16};
  font-weight: ${fontWeight.regular};
  color: ${theme.greyText};
  line-height: 1.5;
  word-break: break-word;

  a {
    color: ${theme.blueText};
    text-decoration: underline;
  }

  strong,
  b {
    color: ${theme.blackText};
  }
`;

/* ── Navigation ────────────────────────────────────────────────────── */

export const PreviousButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 12px 0;
  font-size: ${fontSize.font14};
  font-weight: ${fontWeight.bold};
  color: ${theme.blueText};

  @media ${deviceType.mobile} {
    display: none;
  }

  &:hover {
    opacity: 0.8;
  }

  &:focus-visible {
    outline: 2px solid ${theme.blueText};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

export const PreviousIconWrapper = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: 1.5px solid ${theme.blueText};
  border-radius: 50%;
  transform: rotate(-90deg);
`;

/* ── Loading & Error ───────────────────────────────────────────────── */

export const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
`;

export const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  gap: 16px;
`;

export const ErrorText = styled.p`
  margin: 0;
  font-size: ${fontSize.font14};
  color: ${theme.greyText};
`;

export const RetryButton = styled.button`
  padding: 10px 24px;
  border: 1px solid ${theme.blueText};
  border-radius: 8px;
  background: none;
  font-size: ${fontSize.font14};
  font-weight: ${fontWeight.semiBold};
  color: ${theme.blueText};
  cursor: pointer;

  &:hover {
    background: rgba(0, 140, 255, 0.06);
  }

  &:focus-visible {
    outline: 2px solid ${theme.blueText};
    outline-offset: 2px;
  }
`;
